import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { QuickViewModalShellComponent } from '@clinic-platform/ui-components';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { AuthService } from '../../../core/auth/auth.service';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { ProcedureTypesService } from '../data-access/procedure-types.service';
import { ProcedureTypeDetailItem } from '../data-access/procedure-types.models';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';

@Component({
  selector: 'app-procedure-type-quick-view-modal',
  standalone: true,
  imports: [QuickViewModalShellComponent, TranslatePipe],
  template: `
    <lib-quick-view-modal-shell
      [isOpen]="isOpen && !!procedureTypeId"
      [title]="title | t"
      [subtitle]="subtitle | t"
      [isLoading]="isLoading"
      [loadingText]="'Loading procedure type...' | t"
      [errorText]="errorText ? (errorText | t) : null"
      (close)="close.emit()"
    >
      @if (procedureType) {
        <div class="quick-view-grid">
          <div class="quick-view-field">
            <span>{{ 'Procedure' | t }}</span>
            <strong>{{ procedureType.name }}</strong>
            <div class="quick-view-id-meta">{{ procedureType.id }}</div>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Device Type' | t }}</span>
            <strong>{{ deviceTypeLabel }}</strong>
            @if (procedureType.deviceTypeId) {
              <div class="quick-view-id-meta">{{ procedureType.deviceTypeId }}</div>
            }
          </div>

          <div class="quick-view-field">
            <span>{{ 'Duration' | t }}</span>
            <strong>{{ procedureType.defaultDurationMinutes }} {{ 'min' | t }}</strong>
          </div>

          @if (procedureType.showStatus) {
            <div class="quick-view-field">
              <span>{{ 'Status' | t }}</span>
              <strong>{{ procedureType.isActive ? ('Active' | t) : ('Inactive' | t) }}</strong>
            </div>
          }

          @if (procedureType.showAssignedClinics) {
            <div class="quick-view-field">
              <span>{{ 'Assigned Clinics' | t }}</span>
              <strong>{{ assignedClinicsLabel }}</strong>
            </div>
          }
        </div>
      }
    </lib-quick-view-modal-shell>
  `,
})
export class ProcedureTypeQuickViewModalComponent implements OnChanges {
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly procedureTypesService = inject(ProcedureTypesService);

  @Input() public isOpen = false;
  @Input() public procedureTypeId: string | null = null;
  @Input() public companyIdOverride: string | null = null;
  @Input() public title = 'Procedure Type Quick View';
  @Input() public subtitle = 'Operational procedure context without leaving the current workflow.';

  @Output() public readonly close = new EventEmitter<void>();

  protected isLoading = false;
  protected errorText: string | null = null;
  protected procedureType: ProcedureTypeDetailItem | null = null;

  public ngOnChanges(changes: SimpleChanges): void {
    if ((changes['isOpen'] || changes['procedureTypeId']) && this.isOpen && this.procedureTypeId?.trim()) {
      this.load();
    }
  }

  protected get deviceTypeLabel(): string {
    const normalizedId = this.procedureType?.deviceTypeId?.trim() ?? '';
    if (!normalizedId) {
      return 'No device required';
    }

    return this.procedureType?.deviceTypeName?.trim() || this.readableId(normalizedId);
  }

  protected get assignedClinicsLabel(): string {
    const clinics = this.procedureType?.assignedClinics ?? [];
    if (clinics.length === 0) {
      return 'No assigned clinics';
    }

    if (clinics.length <= 3) {
      return clinics.map((clinic) => clinic.name).join(', ');
    }

    return `${clinics.slice(0, 3).map((clinic) => clinic.name).join(', ')} +${clinics.length - 3}`;
  }

  private load(): void {
    const procedureTypeId = this.procedureTypeId?.trim() ?? '';
    if (!procedureTypeId) {
      return;
    }

    this.isLoading = true;
    this.errorText = null;
    this.procedureType = null;

    this.procedureTypesService.getDetailById(
      procedureTypeId,
      this.resolvedCompanyId ?? undefined,
    ).subscribe({
      next: (procedureType) => {
        this.procedureType = procedureType;
        this.isLoading = false;
      },
      error: (error: unknown) => {
        this.errorText = resolveHttpErrorMessageKey(error, { fallback: 'error.loadFailed' });
        this.isLoading = false;
      },
    });
  }

  private get resolvedCompanyId(): string | null {
    const override = this.companyIdOverride?.trim() ?? '';
    if (override) {
      return override;
    }

    const currentRole = this.authService.getCurrentRole();
    if (currentRole === 'system_admin') {
      return this.systemAdminContext.getCompanyId()?.trim() ?? null;
    }

    return null;
  }

  private readableId(value: string | null | undefined): string {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(normalized)) {
      return `${normalized.slice(0, 8)}...${normalized.slice(-6)}`;
    }

    return normalized;
  }
}
